import { Icon, Input, Notice } from '@speaking-coach/ui'
import { FormalNameFields } from '../common/identity-fields.js'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { queryKeys } from '../api/query-keys'
import { adminClientsApi } from '../clients/api'
import { PageHeader } from '../components/page-header'
import { OPERATOR_FORM_PAGE, OPERATOR_PAGE_MAIN } from '../common/page-layout'
import { SubmitButton } from '../common/save-button'
import { ProvisioningFormActions } from '../common/provisioning-form-actions'
import { UnsavedChangesGuard, useLeaveAfterSave } from '../common/unsaved-changes'
import { CohortAccessFields } from './cohort-access-fields'
import { adminHrUsersApi, messageForHrUserError } from './api'
import { createHrUserSchema, type CreateHrUserForm } from './hr-user-schema'
import type { HrUser } from './types'

export function CreateHrUserPage(): JSX.Element {
  const { clientId = '' } = useParams()
  const [searchParams] = useSearchParams()
  const originatingCohortId = searchParams.get('cohortId')?.trim() || null
  const queryClient = useQueryClient()
  const { leaving, leave } = useLeaveAfterSave()
  const [unmailed, setUnmailed] = useState<HrUser | null>(null)
  // "Add all current cohorts" in flight: creating now would send the
  // selection from before the add (review finding on 780313ac).
  const [cohortsBusy, setCohortsBusy] = useState(false)
  const clientQuery = useQuery({
    queryKey: queryKeys.client(clientId),
    queryFn: () => adminClientsApi.get(clientId),
  })
  const form = useForm<CreateHrUserForm>({
    resolver: zodResolver(createHrUserSchema),
    mode: 'onChange',
    // A cohort-detail entry point carries its cohort in the URL. Seed it as
    // an ordinary selected value so CohortAccessFields resolves the display
    // row through the same client-filtered query used for existing links.
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      cohortIds: originatingCohortId ? [originatingCohortId] : [],
      hrAllCohortAccess: false,
    },
  })
  const { control, handleSubmit, formState } = form
  const allCohorts = form.watch('hrAllCohortAccess')

  const create = useMutation({
    mutationFn: (values: CreateHrUserForm) =>
      adminHrUsersApi.create(clientId, {
        email: values.email.trim(),
        firstName: values.firstName,
        lastName: values.lastName,
        // With the flag on the server derives the set; picks made before
        // it was ticked must not travel as if they described "all".
        cohortIds: values.hrAllCohortAccess ? [] : values.cohortIds,
        hrAllCohortAccess: values.hrAllCohortAccess,
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.client(clientId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.hrUsers })
      if (created.emailSent === false) {
        setUnmailed(created)
        form.reset(form.getValues())
      } else {
        leave(`/admin/clients/${clientId}/hr-users/${created.userId}`)
      }
    },
  })

  return (
    <main className={OPERATOR_PAGE_MAIN}>
      <div className={OPERATOR_FORM_PAGE}>
        <PageHeader title="New HR user" />
        {originatingCohortId !== null && (
          <Link
            to={`/admin/clients/${clientId}/cohorts/${originatingCohortId}`}
            className="mb-4 inline-flex items-center gap-1 text-body text-text-secondary underline"
          >
            <Icon name="back" /> Back to cohort
          </Link>
        )}
        {unmailed && (
          <Notice variant="warning">
            {unmailed.name} was created, but the invitation email was not sent.
            Do not create them again; retry after the HR invitation template is available.
          </Notice>
        )}
        {!unmailed && (
          <section className="bg-surface-card rounded-token-card p-6">
            <form
              onSubmit={handleSubmit((values) => {
                if (!formState.isValid || create.isPending || cohortsBusy) return
                create.mutate(values)
              })}
              className="space-y-4"
              aria-label="New HR user"
              noValidate
            >
              <UnsavedChangesGuard when={formState.isDirty && !leaving} name="new HR user" />
              <Notice variant="neutral">
                <p className="font-medium text-text-primary">
                  What does this create?
                </p>
                <p className="mt-0.5">
                  A read-only HR login. Cohort access controls which students
                  they can see.
                </p>
              </Notice>
              {create.error && (
                <Notice variant="danger">
                  {messageForHrUserError(create.error)}
                </Notice>
              )}
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <label className="block text-body font-medium">
                    Email
                    <Input {...field} type="email" className="mt-1" />
                    {formState.errors.email?.message && (
                      <span className="block text-danger-text text-caption mt-1">
                        {formState.errors.email.message}
                      </span>
                    )}
                  </label>
                )}
              />
              <FormalNameFields
                firstName={form.register('firstName')}
                lastName={form.register('lastName')}
                errors={form.formState.errors}
              />
              <Controller
                control={control}
                name="cohortIds"
                render={({ field }) => (
                  <CohortAccessFields
                    clientId={clientId}
                    clientName={clientQuery.data?.name}
                    selected={field.value}
                    onChange={field.onChange}
                    allCohorts={allCohorts}
                    onAllCohortsChange={(value) =>
                      form.setValue('hrAllCohortAccess', value, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                    onBusyChange={setCohortsBusy}
                  />
                )}
              />
              <ProvisioningFormActions
                cancelTo={`/admin/hr-users?clientId=${encodeURIComponent(clientId)}`}
              >
                {cohortsBusy && (
                  <span role="status" className="text-caption text-text-secondary">
                    Waiting for the cohorts to finish loading…
                  </span>
                )}
                <SubmitButton
                  pending={create.isPending}
                  pendingLabel="Creating…"
                  disabled={!formState.isValid || cohortsBusy}
                >
                  Create HR user
                </SubmitButton>
              </ProvisioningFormActions>
            </form>
          </section>
        )}
      </div>
    </main>
  )
}
